import * as React from "react"
import { useLocation, Link, useParams } from "react-router-dom"
import { cn } from "@/lib/utils"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useIsMobile } from "@/hooks/use-mobile"
import { useBrowserSupport } from "@/hooks/useBrowserSupport"
import {
  Sidebar as ShadcnSidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar"
import WorkspaceSwitcher from "./WorkspaceSwitcher"
import UserDropdown from "./UserDropdown"
import { useExperiments } from "@/hooks/useExperiments"
import { useSiteSafe } from "@/lib/contexts/SiteContext"
import { useWorkspaceSafe } from "@/lib/contexts/WorkspaceContext"

import { useCommandSearch } from "@/components/layout/CommandSearch"
import { useSubscription } from "@/lib/contexts/SubscriptionContext"
import { getQuotaLevel, getQuotaResetDate } from "@/components/shared/plan-usage/eventQuota"
import { formatShortDate } from "@/lib/constants/pricing"
import { NavIcon, type NavIconName } from "./navIcons"

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  isCollapsed: boolean
  setIsCollapsed: (collapsed: boolean) => void
  workspaceSlugId?: string
  propertySlugId?: string
}

// Analytics sub-pages — navigated via AnalyticsTabBar inside the panel; the rail
// shows one "Analytics" row that stays active across all of them (Paper IA).
// Analytics stays active on the five tabs and the retired slugs that
// redirect into them (locations, llm-referrals, forms, bots, insights/*), and
// on visitor profiles opened from the Overview live card.
const ANALYTICS_PATHS = [
  '/traffic',
  '/pages',
  '/channels',
  '/devices',
  '/clicks',
  '/locations',
  '/llm-referrals',
  '/forms',
  '/bots',
  '/insights',
  '/visitors',
] as const

export function Sidebar({
  className,
  isCollapsed,
  setIsCollapsed,
  workspaceSlugId,
  propertySlugId,
}: SidebarProps) {
  const { bannerHeight: offsetTop } = useBrowserSupport()
  const location = useLocation()
  const params = useParams()
  const isMobile = useIsMobile()
  const commandSearch = useCommandSearch()
  const subscription = useSubscription()

  // Resolve a propertyId for experiment count. Both contexts are optional —
  // when the sidebar renders under HubShell (no workspace/site URL params)
  // neither context is mounted, so the hook gracefully returns [].
  const siteCtx = useSiteSafe()
  const workspaceCtx = useWorkspaceSafe()
  const navPropertyId =
    workspaceCtx?.selectedPropertyId ??
    siteCtx?.currentSite?.propertyId ??
    null

  // Experiment count badge — React-Query-cached; dedupes against dashboard queries.
  const { data: navExperiments = [] } = useExperiments({
    propertyId: navPropertyId ?? undefined,
  })
  const activeExperimentCount = React.useMemo(
    () =>
      Array.isArray(navExperiments)
        ? navExperiments.filter((e) => e.status === 'active').length
        : 0,
    [navExperiments]
  )

  // Get shadcn sidebar hook - always called unconditionally
  // SidebarProvider is always present in our app via DashboardLayout
  const sidebarHook = useSidebar()

  // Determine if we're in collapsed mode (only on desktop)
  const collapsed = isCollapsed && !isMobile

  // Desktop: Sync shadcn sidebar state with our local state
  const isShadcnCollapsed = sidebarHook?.state === "collapsed"

  // Sync shadcn sidebar state with our local state (shadcn -> local)
  // This handles when sidebar is toggled via shadcn's internal mechanisms
  React.useEffect(() => {
    if (!isMobile && sidebarHook) {
      const shadcnIsCollapsed = sidebarHook.state === "collapsed"
      if (shadcnIsCollapsed !== collapsed) {
        setIsCollapsed(shadcnIsCollapsed)
      }
    }
  }, [isShadcnCollapsed, sidebarHook, collapsed, isMobile, setIsCollapsed])

  // Get the correct base path from URL params or props
  const getBasePath = () => {
    const wsId = params.workspaceId || workspaceSlugId
    const propId = params.propertySlugId || propertySlugId

    if (wsId) {
      return `/workspace/${wsId}`
    }
    if (propId) {
      return `/p/${propId}`
    }
    return ''
  }

  const basePath = getBasePath()

  // Scope-aware Settings entry. The sidebar footer "Settings" always reflects the
  // scope you're currently in (site vs workspace); account settings lives under the
  // avatar menu. This kills the old ambiguity where one "Settings" label silently
  // pointed at three different pages (and 404'd on the hub with no workspaces).
  // Scope comes from the URL only: on the hub, HubShell passes a default
  // workspace for nav hrefs, but the settings row stays "Account settings".
  const settingsScope: 'site' | 'workspace' | 'account' = params.workspaceId
    ? 'workspace'
    : params.propertySlugId
    ? 'site'
    : 'account'
  const settingsHref =
    settingsScope === 'account' ? '/user-settings' : `${basePath}/settings`
  const settingsLabel =
    settingsScope === 'site'
      ? 'Site settings'
      : settingsScope === 'workspace'
      ? 'Workspace settings'
      : 'Account settings'

  // Check if route is active
  const isActive = (path: string) => {
    const fullPath = `${basePath}${path}`
    return location.pathname === fullPath || location.pathname.startsWith(`${fullPath}/`)
  }
  const settingsActive =
    settingsScope === 'account'
      ? location.pathname.startsWith('/user-settings')
      : isActive('/settings')

  const analyticsActive = ANALYTICS_PATHS.some((path) => isActive(path))

  // NavItem — Paper "App v3": 32px row, 8px radius, 18px duotone glyph, 14px/500
  // label at -0.01em. Active = white row with a hairline ring on the gray canvas.
  // No orange bar, no orange icon, no status dots.
  const NavItem = ({
    href,
    icon,
    label,
    active,
    badge,
    disabled = false,
  }: {
    href: string
    icon: NavIconName
    label: string
    active?: boolean
    badge?: string | number
    disabled?: boolean
  }) => {
    const isExternal = href.startsWith('http')
    const siteParam = new URLSearchParams(location.search).get('site')
    const hrefWithSite = !isExternal && siteParam ? `${href}?site=${siteParam}` : href
    const visualActive = active && !disabled

    const content = (
      <div
        className={cn(
          "relative flex items-center w-full gap-2.5 rounded-lg",
          "transition-colors duration-150 ease-out",
          visualActive
            ? "bg-sidebar-accent shadow-[0_0_0_1px_rgb(0_0_0/0.06),0_1px_2px_rgb(0_0_0/0.06)] dark:bg-white/[0.09] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.1)]"
            : !disabled && "group-hover/nav:bg-black/[0.05] group-active/nav:bg-black/[0.07] dark:group-hover/nav:bg-white/[0.06] dark:group-active/nav:bg-white/[0.08]",
          disabled && "cursor-not-allowed opacity-45",
          collapsed ? "justify-center h-8 w-8 mx-auto" : "h-8 px-2"
        )}
      >
        <NavIcon name={icon} active={visualActive} />

        {!collapsed && (
          <>
            <span
              className={cn(
                "truncate flex-1 text-left text-sm font-medium leading-[18px] tracking-[-0.01em]",
                "transition-colors duration-150",
                visualActive
                  ? "text-foreground"
                  : "text-foreground/[0.62] group-hover/nav:text-foreground"
              )}
            >
              {label}
            </span>

            {/* Count only (e.g. active experiments) — plain muted numeral, never a status dot */}
            {badge !== undefined && badge !== 0 && (
              <span className="shrink-0 text-xs leading-4 tabular-nums text-foreground/60">
                {badge}
              </span>
            )}
          </>
        )}
      </div>
    )

    const linkClass =
      "block group/nav rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring/60"

    const wrappedContent = disabled ? (
      <span className={linkClass} aria-label={label} aria-disabled="true">
        {content}
      </span>
    ) : isExternal ? (
      <a href={href} target="_blank" rel="noopener noreferrer" className={linkClass} aria-label={label}>
        {content}
      </a>
    ) : (
      <Link
        to={hrefWithSite}
        className={linkClass}
        aria-label={label}
        aria-current={active ? "page" : undefined}
      >
        {content}
      </Link>
    )

    if (collapsed || disabled) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{wrappedContent}</TooltipTrigger>
          <TooltipContent side={collapsed ? "right" : "top"} sideOffset={8} className="text-xs font-medium">
            {disabled ? "Finish setup to unlock" : label}
            {badge ? <span className="ml-2 text-muted-foreground">{badge}</span> : null}
          </TooltipContent>
        </Tooltip>
      )
    }

    return wrappedContent
  }

  // Group label — sentence case, 12px regular, 60% ink (Paper: 14px top · 6px bottom · 8px sides).
  const GroupLabel = ({ title }: { title: string }) => {
    if (collapsed) return <div className="h-3" aria-hidden />
    return (
      <div className="px-2 pt-3.5 pb-1.5 text-xs leading-4 text-foreground/60">
        {title}
      </div>
    )
  }

  // ⌘K page search. Questions go to the user's own agent over MCP, not an in-app chat.
  const openSearch = () => commandSearch.setOpen(true)

  const SearchField = () => {
    const field = (
      <button
        type="button"
        onClick={openSearch}
        aria-label="Search (⌘K)"
        className={cn(
          "flex items-center shrink-0 rounded-lg bg-sidebar-accent text-left",
          "shadow-[0_0_0_1px_rgb(0_0_0/0.06),0_1px_2px_rgb(0_0_0/0.03)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08)]",
          "transition-shadow hover:shadow-[0_0_0_1px_rgb(0_0_0/0.1),0_1px_2px_rgb(0_0_0/0.04)]",
          "outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring/60",
          collapsed ? "h-8 w-8 justify-center mx-auto" : "h-8 w-full gap-2 px-2.5"
        )}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" className="shrink-0 stroke-foreground/35">
          <circle cx="11" cy="11" r="7" fill="none" strokeWidth="2.14" />
          <path d="m20 20-3.5-3.5" fill="none" strokeWidth="2.14" strokeLinecap="round" />
        </svg>
        {!collapsed && (
          <>
            <span className="flex-1 truncate text-[13px] leading-4 text-foreground/40">Search</span>
            <kbd className="font-sans text-xs font-medium leading-4 text-foreground/[0.42]">⌘K</kbd>
          </>
        )}
      </button>
    )
    if (!collapsed) return field
    return (
      <Tooltip>
        <TooltipTrigger asChild>{field}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={8} className="text-xs font-medium">
          Search <span className="ml-1 text-muted-foreground">⌘K</span>
        </TooltipContent>
      </Tooltip>
    )
  }

  // Plan block — Paper: #EDEDED well, 10px radius, 12px padding, ink progress on #DADADA.
  // Quota states follow the urgency scale (Paper "Event quota 75% / 100%",
  // "Read-only after cancel"): amber from 75%, rust when the cap pauses tracking.
  const PlanBlock = () => {
    if (collapsed || subscription.isLoading || subscription.eventLimit <= 0) return null
    const pct = Math.min(100, Math.max(0, subscription.eventUsagePercent))
    const readOnly = subscription.isReadOnly
    const level = readOnly ? 'ok' : getQuotaLevel(subscription.eventsUsed, subscription.eventLimit)
    const resets = formatShortDate(getQuotaResetDate())
    const usage = `${formatCompact(subscription.eventsUsed)} of ${formatCompact(subscription.eventLimit)}`
    return (
      <div className="flex shrink-0 flex-col gap-2.5 rounded-[10px] bg-[#EDEDED] p-3 dark:bg-white/[0.06]">
        <div className="flex items-center justify-between gap-2 text-xs leading-4">
          <span className="truncate font-medium text-foreground/90">
            {subscription.planName} plan{readOnly ? ' · ended' : ''}
          </span>
          <span
            className={cn(
              'shrink-0 tabular-nums',
              level === 'warning' && 'font-medium text-urgency-amber-fg',
              level === 'full' && 'font-medium text-urgency-rust-fg',
              level === 'ok' && 'text-foreground/60',
            )}
          >
            {readOnly ? 'Read-only' : usage}
          </span>
        </div>
        <div
          className="h-1 rounded-[2px] bg-[#DADADA] dark:bg-white/10"
          role="progressbar"
          aria-label="Data points used this month"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pct)}
        >
          <div
            className={cn(
              'h-1 rounded-[2px]',
              level === 'warning' ? 'bg-urgency-amber-accent' : level === 'full' ? 'bg-urgency-rust-accent' : 'bg-foreground',
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className={cn('text-xs leading-4', level === 'full' ? 'text-urgency-rust-fg' : 'text-foreground/60')}>
          {readOnly
            ? 'Tracking stopped'
            : level === 'full'
              ? `Tracking paused${resets ? ` · resets ${resets}` : ''}`
              : level === 'warning'
                ? `${Math.round(pct)}% of data points used${resets ? ` · resets ${resets}` : ''}`
                : 'Data points this month'}
        </span>
      </div>
    )
  }

  const handleCollapseToggle = () => {
    if (sidebarHook) sidebarHook.toggleSidebar()
    else setIsCollapsed(!isCollapsed)
  }

  return (
    <ShadcnSidebar
      // `inset` (White signal v2): the rail sits on the gray canvas with no
      // border, and SidebarInset floats the content on one white 12px panel.
      variant="inset"
      collapsible="icon"
      className={cn("hidden md:flex", className)}
      style={offsetTop > 0 ? { insetBlockStart: offsetTop, height: `calc(100svh - ${offsetTop}px)` } : undefined}
    >
      {/* Rail: 12px top/bottom/left, 10px right, 14px between blocks (Paper). */}
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col gap-3.5 py-3",
          collapsed ? "px-2" : "pl-3 pr-2.5"
        )}
      >
        <SidebarHeader className="p-0 gap-3.5 shrink-0">
          <WorkspaceSwitcher isCollapsed={collapsed} />
          <SearchField />
        </SidebarHeader>

        <SidebarContent className="gap-0 overflow-visible">
          <ScrollArea className="min-h-0 flex-1 -m-1">
            <nav aria-label="Main" className="flex flex-col gap-0.5 p-1">
              <NavItem
                href={subscription.isAgentPlan ? "/agent" : "/dashboard"}
                icon="home"
                label="Home"
                active={location.pathname === (subscription.isAgentPlan ? "/agent" : "/dashboard")}
              />

              {/* Agent plans: the agent does observe/optimize through MCP; the app is account management. */}
              {!subscription.isAgentPlan && (
                <>
              {/* Observe — traffic → behavior → money. Analytics sub-pages live in AnalyticsTabBar. */}
              <GroupLabel title="Observe" />
              <NavItem
                href={`${basePath}/traffic`}
                icon="analytics"
                label="Analytics"
                active={analyticsActive}
              />
              <NavItem
                href={`${basePath}/heatmaps`}
                icon="heatmaps"
                label="Heatmaps"
                active={isActive('/heatmaps')}
              />
              <NavItem
                href={`${basePath}/funnel-analyzer`}
                icon="funnels"
                label="Funnels"
                active={isActive('/funnel-analyzer') || isActive('/funnels')}
              />
              <NavItem
                href={`${basePath}/attribution`}
                icon="attribution"
                label="Attribution"
                active={isActive('/attribution')}
              />

              {/* Optimize — change the site */}
              <GroupLabel title="Optimize" />
              <NavItem
                href={`${basePath}/split-testing`}
                icon="experiments"
                label="Experiments"
                active={isActive('/split-testing') || isActive('/split-tests') || isActive('/experiments') || isActive('/test-planner')}
                badge={activeExperimentCount > 0 ? activeExperimentCount : undefined}
              />
                </>
              )}

              {/* Agent & data — API/MCP surface, integrations in, export out */}
              <GroupLabel title="Agent & data" />
              <NavItem
                href={`${basePath}/api`}
                icon="api"
                label="API & MCP"
                active={isActive('/api')}
              />
              <NavItem
                href={`${basePath}/connectors`}
                icon="connectors"
                label="Connectors"
                active={isActive('/connectors')}
              />
              {!subscription.isAgentPlan && (
                <NavItem
                  href={`${basePath}/export`}
                  icon="export"
                  label="Export"
                  active={isActive('/export')}
                />
              )}
            </nav>
          </ScrollArea>
        </SidebarContent>

        <SidebarFooter className="p-0 gap-3.5 shrink-0">
          {/* Meta items — occasional use */}
          <div className="flex flex-col gap-0.5">
            <NavItem
              href={`${basePath}/tools`}
              icon="tools"
              label="Marketing tools"
              active={isActive('/tools')}
            />
            <NavItem
              href={`${basePath}/resources`}
              icon="resources"
              label="Resources"
              active={isActive('/resources')}
            />
            <NavItem
              href={settingsHref}
              icon="settings"
              label={settingsLabel}
              active={settingsActive}
            />
          </div>

          <PlanBlock />

          <UserDropdown
            isCollapsed={collapsed}
            onToggleCollapse={isMobile ? undefined : handleCollapseToggle}
          />
        </SidebarFooter>
      </div>
    </ShadcnSidebar>
  )
}

function formatCompact(n: number): string {
  if (!Number.isFinite(n)) return '0'
  if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`
  return String(Math.round(n))
}

export default Sidebar
